import {
  appendFileSync,
  chmodSync,
  existsSync,
  mkdirSync,
  readFileSync,
  statSync,
  truncateSync,
} from 'node:fs';
import { homedir } from 'node:os';
import { dirname, join } from 'node:path';
import { Methods, Notifications } from '../api/methods.js';
import { redact } from './redact.js';

const MAX_TRACE_BYTES = 2_000_000;

const SENSITIVE_REQUEST_METHODS = new Set<string>([
  Methods.Run,
  Methods.ReadersWrite,
  Methods.InputKeyboard,
  Methods.InputGamepad,
  Methods.SettingsAuthClaim,
  Methods.ProfilesNew,
  Methods.ProfilesUpdate,
  Methods.ProfilesSwitch,
  Methods.ProfilesVerify,
]);

const SENSITIVE_DATA_METHODS = new Set<string>([Methods.Tokens, Methods.TokensHistory]);

export interface TraceEntry {
  timestamp: string;
  deviceId: string;
  direction: 'request' | 'response' | 'notification';
  method: string;
  id?: string;
  data: unknown;
  durationMs?: number;
}

export function defaultTracePath(): string {
  return join(
    process.env.XDG_CACHE_HOME ?? join(homedir(), '.cache'),
    'zaparoo-cli',
    'trace.jsonl',
  );
}

export class TraceWriter {
  constructor(private readonly path = defaultTracePath()) {}

  write(entry: TraceEntry): void {
    const dir = dirname(this.path);
    if (!existsSync(dir)) mkdirSync(dir, { recursive: true, mode: 0o700 });
    if (existsSync(this.path) && statSync(this.path).size > MAX_TRACE_BYTES)
      truncateSync(this.path, 0);
    appendFileSync(this.path, `${JSON.stringify(redactTraceEntry(entry))}\n`, { mode: 0o600 });
    chmodSync(this.path, 0o600);
  }

  readLast(count: number): TraceEntry[] {
    if (!existsSync(this.path)) return [];
    return readFileSync(this.path, 'utf8')
      .trim()
      .split('\n')
      .filter(Boolean)
      .slice(-count)
      .flatMap((line) => {
        try {
          return [JSON.parse(line) as TraceEntry];
        } catch {
          return [];
        }
      });
  }
}

function redactTraceEntry(entry: TraceEntry): TraceEntry {
  const redacted = redact(entry) as TraceEntry;
  if (entry.direction === 'request' && SENSITIVE_REQUEST_METHODS.has(entry.method)) {
    return { ...redacted, data: '[REDACTED]' };
  }
  if (
    (entry.direction === 'response' && SENSITIVE_DATA_METHODS.has(entry.method)) ||
    (entry.direction === 'notification' && entry.method === Notifications.TokensAdded)
  ) {
    return { ...redacted, data: '[REDACTED]' };
  }
  return redacted;
}
